import * as React from 'react';
import pluralize from 'pluralize';

import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import {
  ComposableTable,
  getTextBadgeCellGenerator,
  getTextCellGenerator,
} from 'components/ComposableTable';
import { statusToTextBadge } from 'admin/constants/CsvValidations';

import { SyncType } from 'globalTypes';

type Validation = {
  fileName: string;
  status: string;
  rowErrorCount: number;
  downloadUrl: string;
};

export type Props = {
  syncType: SyncType;
  validations: Validation[];
};

const COLUMNS = [{ name: 'CSV' }, { name: 'Status' }, { name: 'Next steps' }];

export default class CsvValidationTable extends React.Component<Props> {
  formattingUrl = (syncType: SyncType): string => {
    if (syncType === SyncType.oneRosterSpreadsheet) {
      return LinkConstants.FORMAT_ONEROSTER;
    }

    if (syncType === SyncType.remindSpreadsheet) {
      return LinkConstants.FORMAT_REMIND;
    }

    if (syncType === SyncType.remindSpreadsheetV2) {
      return LinkConstants.FORMAT_RESOLVE_REMINDV2;
    }

    return '';
  };

  renderNextSteps = (validation: Validation): string | React.ReactElement => {
    const { syncType } = this.props;
    const { downloadUrl, rowErrorCount, status } = validation;

    if (status === 'incorrect_headers') {
      return (
        <Link href={downloadUrl} target="_blank">
          {`Download ${rowErrorCount} invalid ${pluralize('header', rowErrorCount)}`}
        </Link>
      );
    }

    if (status === 'invalid_rows') {
      return (
        <Link href={downloadUrl} target="_blank">
          {`Download ${rowErrorCount} row ${pluralize('error', rowErrorCount)}`}
        </Link>
      );
    }

    if (status === 'missing') {
      return 'Include this CSV file in the next upload';
    }

    if (status === 'unreadable') {
      return (
        <Link href={this.formattingUrl(syncType)} target="_blank">
          Learn how to format the file
        </Link>
      );
    }

    return '';
  };

  statusText = (status: string): string => {
    if (status === 'incorrect_headers') {
      return 'Invalid headers';
    }
    if (status === 'invalid_rows') {
      return 'Invalid row content';
    }
    if (status === 'missing') {
      return 'Required file missing';
    }
    if (status === 'not_uploaded') {
      return 'Not uploaded';
    }
    if (status === 'unreadable') {
      return 'Unreadable file';
    }
    if (status === 'valid') {
      return 'Formatted correctly';
    }
    return '';
  };

  columnGenerators = () => {
    return [
      getTextCellGenerator('fileName'),
      getTextBadgeCellGenerator('status', statusToTextBadge, (status: string) =>
        this.statusText(status)
      ),
      getTextCellGenerator((row: Validation) => this.renderNextSteps(row)),
    ];
  };

  render() {
    const { validations } = this.props;

    return (
      <ComposableTable
        columnGenerators={this.columnGenerators()}
        columnHeaders={COLUMNS}
        keyField={(row: Validation) => row.fileName}
        rows={validations}
        className="CsvValidationTable"
      />
    );
  }
}
